import { CircleCheck } from 'lucide-react'
import { Link } from 'react-router'

import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'

/** How long a filed toast stays up. The countdown bar runs the same length, so the two never disagree. */
export const FILED_TOAST_MS = 5000

/** A read past this is worth admitting to; past the second, worth giving up on. */
export const SLOW_READ_MS = 60_000
export const GIVE_UP_READ_MS = 120_000

export const UNREADABLE_MESSAGE = 'Unable to read that file. Please try again.'
export const GAVE_UP_MESSAGE = 'File reading failed. Please try again.'
const SLOW_MESSAGE = 'Reading this file is taking a bit. Trying for one more minute…'

/** One id for the whole read, so the drop and its progress replace each other's toast. */
export const REVIEWING_TOAST_ID = 'vault-drop-reviewing'

function ToastShell({ countdownMs, children }: { countdownMs?: number; children: React.ReactNode }) {
  return (
    <div className="bg-background relative w-full overflow-hidden rounded-md border shadow-lg">
      {countdownMs !== undefined && (
        <span
          aria-hidden="true"
          style={{ '--toast-countdown-duration': `${countdownMs}ms` } as React.CSSProperties}
          className="bg-accent-green animate-toast-countdown absolute inset-x-0 top-0 h-1 origin-left"
        />
      )}
      <div className="flex items-center gap-3 px-4 py-3.5">{children}</div>
    </div>
  )
}

/** Up from the moment the files land until the parser has read every one of them. */
export function ReviewingToast({ count, slow }: { count: number; slow?: boolean }) {
  return (
    <ToastShell>
      <Spinner className="text-muted-foreground size-4 shrink-0" />
      <span className="text-sm font-medium">
        {slow ? SLOW_MESSAGE : count === 1 ? 'Reviewing your document…' : `Reviewing ${count} documents…`}
      </span>
    </ToastShell>
  )
}

/** Where one upload ended up, and the way to go look at it. */
export function FiledToast({
  subjectName,
  slotLabel,
  to,
  onView,
}: {
  subjectName: string
  slotLabel: string
  /** The file the document landed in — a driver, a truck, the carrier itself. */
  to: string
  onView: () => void
}) {
  return (
    <ToastShell countdownMs={FILED_TOAST_MS}>
      <CircleCheck className="text-accent-green size-4 shrink-0" />
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-sm font-medium">Filed under {subjectName}</span>
        <span className="text-muted-foreground truncate text-sm">{slotLabel}</span>
      </span>
      <Button asChild size="sm" variant="outline" className="shrink-0" onClick={onView}>
        <Link to={to}>View</Link>
      </Button>
    </ToastShell>
  )
}
